// src/pages/mock-data-management-dashboard/components/DataGenerationPanel.jsx
import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const DataGenerationPanel = ({ activeTab, config, onGenerate, onConfigChange }) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const getEntityDisplayName = (entityType) => {
    const nameMap = {
      users: 'Users',
      biodataRecords: 'Biodata Records',
      documents: 'Documents',
      systemLogs: 'System Logs'
    };
    return nameMap[entityType] || entityType;
  };

  const getEntityIcon = (entityType) => {
    const iconMap = {
      users: 'Users',
      biodataRecords: 'FileText',
      documents: 'FileImage',
      systemLogs: 'Activity'
    };
    return iconMap[entityType] || 'Database';
  };

  const getQuantityLimits = (entityType) => {
    const limitsMap = {
      users: { min: 1, max: 1000, step: 1, default: 10 },
      biodataRecords: { min: 1, max: 5000, step: 5, default: 50 },
      documents: { min: 1, max: 1000, step: 1, default: 25 },
      systemLogs: { min: 10, max: 10000, step: 10, default: 100 }
    };
    return limitsMap[entityType] || { min: 1, max: 100, step: 1, default: 10 };
  };

  const getPatternOptions = (entityType) => {
    const commonPatterns = [
      { value: 'realistic', label: 'Realistic Data', description: 'Production-like data patterns' },
      { value: 'random', label: 'Random Data', description: 'Completely randomized values' },
      { value: 'edge_cases', label: 'Edge Cases', description: 'Boundary and edge case scenarios' }
    ];

    const specificPatterns = {
      users: [
        ...commonPatterns,
        { value: 'role_balanced', label: 'Role Balanced', description: 'Equal distribution across roles' },
        { value: 'new_users', label: 'New Users', description: 'Recently registered accounts' }
      ],
      biodataRecords: [
        ...commonPatterns,
        { value: 'pending_review', label: 'Pending Review', description: 'Records awaiting approval' },
        { value: 'complete_profiles', label: 'Complete Profiles', description: 'Fully completed biodata' }
      ],
      documents: [
        ...commonPatterns,
        { value: 'verification_needed', label: 'Needs Verification', description: 'Unverified documents' },
        { value: 'expiring_soon', label: 'Expiring Soon', description: 'Documents nearing expiry' }
      ],
      systemLogs: [
        ...commonPatterns,
        { value: 'error_heavy', label: 'Error Heavy', description: 'Higher proportion of errors' },
        { value: 'recent_activity', label: 'Recent Activity', description: 'Last 24 hours activity' }
      ]
    };

    return specificPatterns[entityType] || commonPatterns;
  };

  const handleQuantityChange = (value) => {
    const newConfig = { ...config, quantity: parseInt(value) };
    onConfigChange(newConfig);
  };

  const handleRandomizationToggle = () => {
    const newConfig = { ...config, randomization: !config.randomization };
    onConfigChange(newConfig);
  };

  const handlePatternChange = (pattern) => {
    const newConfig = { ...config, pattern };
    onConfigChange(newConfig);
  };

  const handleGenerate = async (append = false) => {
    setIsGenerating(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate generation time
      onGenerate(activeTab, { ...config, append });
    } finally {
      setIsGenerating(false);
    }
  };

  const limits = getQuantityLimits(activeTab);
  const patterns = getPatternOptions(activeTab);
  const selectedPattern = patterns.find(p => p.value === config.pattern) || patterns[0];

  return (
    <div className="card p-6 h-fit">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
          <Icon name={getEntityIcon(activeTab)} size={20} className="text-primary-600" />
        </div>
        <div>
          <h3 className="font-semibold text-text-primary">
            Generate {getEntityDisplayName(activeTab)}
          </h3>
          <p className="text-sm text-text-secondary">Configure data generation parameters</p>
        </div>
      </div>

      {/* Quantity Slider */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <label className="text-sm font-medium text-text-primary">Quantity</label>
          <span className="text-sm font-semibold text-primary">{config.quantity}</span>
        </div>
        <input
          type="range"
          min={limits.min}
          max={limits.max}
          step={limits.step}
          value={config.quantity}
          onChange={(e) => handleQuantityChange(e.target.value)}
          className="w-full h-2 bg-secondary-200 rounded-lg appearance-none cursor-pointer slider"
        />
        <div className="flex justify-between text-xs text-text-secondary mt-1">
          <span>{limits.min}</span>
          <span>{limits.max}</span>
        </div>
      </div>

      {/* Randomization Toggle */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-text-primary">Randomization</label>
            <p className="text-xs text-text-secondary">Enable random data variation</p>
          </div>
          <button
            onClick={handleRandomizationToggle}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              config.randomization ? 'bg-primary' : 'bg-secondary-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                config.randomization ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {/* Data Pattern Selection */}
      <div className="mb-6">
        <label className="text-sm font-medium text-text-primary mb-3 block">Data Pattern</label>
        <div className="space-y-2">
          {patterns.map((pattern) => (
            <button
              key={pattern.value}
              onClick={() => handlePatternChange(pattern.value)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                config.pattern === pattern.value
                  ? 'border-primary bg-primary-50 text-primary' :'border-secondary-200 hover:border-secondary-300 text-text-secondary'
              }`}
            >
              <div className="font-medium text-sm">{pattern.label}</div>
              <div className="text-xs opacity-75">{pattern.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Generation Buttons */}
      <div className="space-y-3">
        <button
          onClick={() => handleGenerate(false)}
          disabled={isGenerating}
          className="w-full btn btn-primary flex items-center justify-center space-x-2"
        >
          {isGenerating ? (
            <>
              <Icon name="Loader2" size={16} className="animate-spin" />
              <span>Generating...</span>
            </>
          ) : (
            <>
              <Icon name="RefreshCw" size={16} />
              <span>Generate New</span>
            </>
          )}
        </button>
        
        <button
          onClick={() => handleGenerate(true)}
          disabled={isGenerating}
          className="w-full btn btn-secondary flex items-center justify-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Append Data</span>
        </button>
      </div>

      {/* Configuration Summary */}
      <div className="mt-6 p-3 bg-secondary-50 rounded-lg">
        <div className="text-xs text-text-secondary space-y-1">
          <div className="flex justify-between">
            <span>Pattern:</span>
            <span className="font-medium">{selectedPattern.label}</span>
          </div>
          <div className="flex justify-between">
            <span>Randomization:</span>
            <span className="font-medium">{config.randomization ? 'Enabled' : 'Disabled'}</span>
          </div>
          <div className="flex justify-between">
            <span>Quantity:</span>
            <span className="font-medium">{config.quantity} records</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataGenerationPanel;